// EditableMaterial — a material's document in its EDITING face, pure.
//
// One wiring of `MaterialDocument`'s `on*` editing callbacks to the editor
// host's handlers, shared by the two places a material is edited: the
// library detail (edit in place, library layout 2026-10-01) and the legacy
// material tab. Each callback is an intent the editor host maps to its ONE
// existing command (updateElementText, removeElement, moveElement, …) with
// the same optimistic apply + content-hash lock; this component decides and
// mutates nothing itself. There is no separate save step: every edit is
// committed as it lands, exactly as in the material tab.
//
// A blank material (one empty prose seed, or no blocks) shows the invite to
// insert a first block instead of an empty document, unless it is a module:
// its parts (included resources) follow the document as a list, and a module
// with nothing but parts shows just those.
//
// A Studio page writes on the paper instead (`blankFace: "document"`; pen
// "Always writing", piece 5): every page, a course or module page too, ends
// in the writing line, an empty line saying "Write, or type / for more" with
// the hint under it. Clicking it, or the paper below it, starts a paragraph
// there with the caret in it (unless the last block already is an empty
// one, which then is the line). A course's or module's parts stand among
// the text where they are (`renderPart`, the Studio's tiles).

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../Button/Button.js";
import { EditableText } from "../Editor/EditableText/EditableText.js";
import { ElementBlock } from "../Editor/ElementBlock/ElementBlock.js";
import {
    MaterialDocument,
    type SlashMenuState,
} from "../Editor/MaterialDocument/MaterialDocument.js";
import { defaultRenderers } from "../Editor/renderers.js";
import type { Element, PartElement } from "../Editor/types.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { type ModulePart, ModuleParts } from "../ModuleParts/ModuleParts.js";
import type { FocusTarget } from "../Navigation/types.js";
import type { EditorHostApi } from "./types.js";

// The slice of the editor host this face reads: the loaded content + wear,
// and the editing handlers.
export type MaterialEditing = Pick<
    EditorHostApi,
    | "loop"
    | "pathById"
    | "slashTrigger"
    | "slashMenuState"
    | "autoEditElementId"
    | "onAutoEditConsumed"
    | "onReview"
    | "onEditText"
    | "onEditFeedback"
    | "onDraftText"
    | "onDraftFeedback"
    | "onMarkChoiceCorrect"
    | "onUnmarkChoiceCorrect"
    | "onAddChoice"
    | "onEndQuestion"
    | "onRemoveChoice"
    | "onMoveChoice"
    | "onAllowSingleAnswer"
    | "onAllowMultipleAnswers"
    | "onSetHeadingLevel"
    | "onMoveElement"
    | "onRemoveElement"
    | "onInputRule"
    | "onSlashTrigger"
    | "onSlashNavigate"
    | "onSlashCommitActive"
    | "onEnterBlockBelow"
    | "onDeleteEmptyBlock"
    | "onStartWriting"
    | "onWriteAbove"
>;

const isBlankMaterial = (elements: readonly Element[]): boolean =>
    elements.every(
        (el) =>
            el.type === "part" ||
            (el.type === "prose" && el.markdown.trim() === ""),
    );

// The last block is an empty paragraph: it is the writing line already.
const endsEmpty = (elements: readonly Element[]): boolean => {
    const last = elements.at(-1);
    return last?.type === "prose" && last.markdown === "";
};

// Offline, the "/" menu's rows show but pick nothing, and a line that asks
// does not send (its words stay).
const offlineMenu = (
    menu: SlashMenuState | undefined,
): SlashMenuState | undefined => {
    if (menu === undefined) return undefined;
    switch (menu.type) {
        case "open":
            return { ...menu, disabled: true };
        case "asking":
            return { ...menu, online: false };
        case "held":
            return menu;
        default:
            return exhaustSwitchCase(menu);
    }
};

export const EditableMaterial = ({
    elements,
    editor,
    onInsert,
    onJumpToSource,
    target,
    parts,
    onOpenPart,
    blankFace = "invite",
    onPasteTooLong,
    online = true,
    renderPart,
    add,
}: {
    // A part among the text (the Studio's tile; absent: the parts follow
    // the document as a list).
    readonly renderPart?: (part: PartElement) => ReactNode;
    // The + in the writing line's margin (the Studio's Create menu).
    readonly add?: ReactNode;
    // Offline: "/" picks nothing and Ask here does not send.
    readonly online?: boolean;
    readonly elements: readonly Element[];
    // What a blank material shows: the invite to insert a first block, or
    // its (empty) blocks to write in (a Studio page's introduction).
    readonly blankFace?: "invite" | "document";
    // A module's parts in reading order (empty: not a module).
    readonly parts: readonly ModulePart[];
    readonly onOpenPart: (id: string) => void;
    readonly editor: MaterialEditing;
    // Open the block palette (the editor's one insert surface).
    readonly onInsert: () => void;
    readonly onJumpToSource: () => void;
    // Where to open the document at (a review item's element), once.
    readonly target?: FocusTarget;
    // A paste past a block's ceiling (absent: the block keeps what fits).
    readonly onPasteTooLong?: (text: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const partsList =
        parts.length > 0 && renderPart === undefined ? (
            <ModuleParts parts={parts} onOpen={onOpenPart} />
        ) : null;
    if (blankFace === "invite" && isBlankMaterial(elements))
        return (
            partsList ?? (
                <div className="bilda-material-empty">
                    <EmptyState
                        title={t`Add your first question`}
                        body={t`This material is empty. Insert a block to begin, or ask the assistant to draft one.`}
                    >
                        <Button variant="primary" onClick={onInsert}>
                            {t`Insert`}
                        </Button>
                    </EmptyState>
                </div>
            )
        );
    // The writing line and its hint (a Studio page).
    const end =
        blankFace === "document" ? (
            <div className="bilda-writing-line">
                {endsEmpty(elements) ? null : (
                    <ElementBlock
                        authoredBy={{ type: "human" }}
                        body={
                            <EditableText
                                value=""
                                ariaLabel={t`Write`}
                                className="bilda-prose bilda-writing-line__text"
                                placeholder={t`Write, or type / for more`}
                                onCommit={() => undefined}
                                onActivate={editor.onStartWriting}
                            />
                        }
                    />
                )}
                {/* After the line, so it paints over the line's rule. */}
                {add === undefined || endsEmpty(elements) ? null : (
                    <div className="bilda-writing-line__margin">{add}</div>
                )}
                <p className="bilda-writing-line__hint">
                    {t`Enter starts a new line · / opens the menu where you are`}
                </p>
                {endsEmpty(elements) ? null : (
                    // The empty paper below: a click there writes too (the
                    // line above is the keyboard's way in).
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-hidden
                        className="bilda-writing-line__below"
                        onClick={() => editor.onStartWriting()}
                    />
                )}
            </div>
        ) : undefined;
    return (
        <>
            <MaterialDocument
                ariaLabel={t`Material`}
                elements={elements}
                renderers={defaultRenderers}
                annotations={editor.loop.annotations}
                reviewOpenFor={editor.loop.reviewOpenFor}
                onReview={editor.onReview}
                onEditText={editor.onEditText}
                onEditFeedback={editor.onEditFeedback}
                onDraftText={editor.onDraftText}
                onDraftFeedback={editor.onDraftFeedback}
                onMarkChoiceCorrect={editor.onMarkChoiceCorrect}
                onUnmarkChoiceCorrect={editor.onUnmarkChoiceCorrect}
                onAddChoice={editor.onAddChoice}
                onEndQuestion={editor.onEndQuestion}
                onRemoveChoice={editor.onRemoveChoice}
                onMoveChoice={editor.onMoveChoice}
                onAllowSingleAnswer={editor.onAllowSingleAnswer}
                onAllowMultipleAnswers={editor.onAllowMultipleAnswers}
                onSetHeadingLevel={editor.onSetHeadingLevel}
                onMoveElement={editor.onMoveElement}
                onRemoveElement={editor.onRemoveElement}
                onJumpToSource={onJumpToSource}
                onInputRule={editor.onInputRule}
                slashTrigger={
                    editor.slashTrigger !== null
                        ? {
                              forElementId: editor.slashTrigger.forElementId,
                              state: editor.slashTrigger.state,
                          }
                        : undefined
                }
                onSlashTrigger={editor.onSlashTrigger}
                onSlashNavigate={editor.onSlashNavigate}
                onSlashCommitActive={
                    online ? editor.onSlashCommitActive : () => false
                }
                onEnterAtEnd={editor.onEnterBlockBelow}
                onBackspaceEmpty={editor.onDeleteEmptyBlock}
                onPasteTooLong={onPasteTooLong}
                slashMenu={
                    online
                        ? editor.slashMenuState
                        : offlineMenu(editor.slashMenuState)
                }
                autoEditElementId={editor.autoEditElementId ?? undefined}
                onAutoEditConsumed={editor.onAutoEditConsumed}
                revealTarget={target}
                pathById={editor.pathById}
                renderPart={renderPart}
                start={
                    // A page that starts with a part: a quiet line above it
                    // to write there.
                    blankFace === "document" && elements[0]?.type === "part" ? (
                        <button
                            type="button"
                            className="bilda-writing-line__above"
                            aria-label={t`Write above`}
                            onClick={() => editor.onWriteAbove()}
                        />
                    ) : undefined
                }
                end={end}
            />
            {partsList}
        </>
    );
};
